import { useEffect, useState, useMemo } from "react";
import api from "@/api/client";
import PageShell from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import ServerPagination from "@/components/ServerPagination";
import BookParcelDialog from "@/components/BookParcelDialog";
import { PackageOpen, Download, Truck, Mail, Search, ClipboardList, Zap } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  not_booked: "border-slate-300 text-slate-600",
  pickup_requested: "border-blue-500 text-blue-600 bg-blue-50",
  pending_pickup: "border-indigo-500 text-indigo-600 bg-indigo-50",
  pickup_delayed: "border-amber-500 text-amber-700 bg-amber-50",
  picked_up: "border-emerald-500 text-emerald-700 bg-emerald-50",
  booked: "border-blue-500 text-blue-600 bg-blue-50",
  courier_notified: "border-amber-500 text-amber-700 bg-amber-50",
  booking_completed: "border-emerald-600 text-emerald-700 bg-emerald-50",
  in_transit: "border-emerald-500 text-emerald-700 bg-emerald-50",
  delivered: "border-green-600 text-green-700 bg-green-50",
  cancelled: "border-red-500 text-red-700 bg-red-50",
};

// Editable booking-status transitions (spec)
const BOOKING_STATUS_CHOICES = [
  "pickup_requested",
  "pending_pickup",
  "pickup_delayed",
  "picked_up",
  "booking_completed",
  "cancelled",
];

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LSKEY = "bookings.pageSize";
const LSFILTER = "bookings.filters";

export default function BookingsPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem(LSKEY) || "50", 10));
  const [tab, setTab] = useState("book"); // "book" | "history"

  const savedFilters = (() => { try { return JSON.parse(localStorage.getItem(LSFILTER) || "{}"); } catch { return {}; } })();
  const [q, setQ] = useState(savedFilters.q || "");
  const [fStatus, setFStatus] = useState(savedFilters.fStatus || "");
  const [fCourier, setFCourier] = useState(savedFilters.fCourier || "");
  const [fStart, setFStart] = useState(savedFilters.fStart || "");
  const [fEnd, setFEnd] = useState(savedFilters.fEnd || "");
  const [includeArchive, setIncludeArchive] = useState(false);

  const load = async (nextPage = page, nextSize = pageSize) => {
    const params = { page: nextPage, page_size: nextSize, include_archive: includeArchive };
    if (q) params.q = q;
    if (fStatus) params.status = fStatus;
    if (fCourier) params.courier = fCourier;
    if (fStart) params.start = fStart;
    if (fEnd) params.end = fEnd;
    try {
      const [{ data }, { data: kpi }] = await Promise.all([
        api.get("/bookings", { params }),
        api.get("/dashboard/pickup-booking-stats").catch(() => ({ data: null })),
      ]);
      setItems(data.items || []);
      setTotal(data.total || 0); setPages(data.pages || 1);
      setStats(kpi);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { load(1, pageSize); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(page, pageSize); /* eslint-disable-next-line */ }, [page, pageSize]);
  const applyFilters = () => {
    localStorage.setItem(LSFILTER, JSON.stringify({ q, fStatus, fCourier, fStart, fEnd }));
    setPage(1); load(1, pageSize);
  };
  const resetFilters = () => {
    setQ(""); setFStatus(""); setFCourier(""); setFStart(""); setFEnd("");
    localStorage.removeItem(LSFILTER); setPage(1); setTimeout(() => load(1, pageSize), 0);
  };

  const setBookingStatus = async (id, booking_status, bnum) => {
    try {
      await api.patch(`/bookings/${id}`, { booking_status });
      toast.success(
        `${bnum} → ${booking_status.replaceAll("_", " ")}`,
        booking_status === "booking_completed"
          ? { description: "SLA started. Shipment lifecycle active." }
          : undefined
      );
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadManifest = async (b) => {
    const url = `${REACT_APP_BACKEND_URL}/api/bookings/${b.id}/manifest.pdf`;
    const token = localStorage.getItem("mr_token");
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `PickupManifest_${b.booking_number}.pdf`;
    a.click();
  };

  return (
    <PageShell
      title="Bookings"
      subtitle="Courier bookings created from outward parcels · Pickup Manifest and SLA tracked"
      overline="Booking module"
    >
      {/* Tabs: Book Parcel · Booking History */}
      <div className="flex items-center gap-1 border-b border-border" data-testid="bookings-tabs">
        <button
          data-testid="tab-book-parcel"
          onClick={() => setTab("book")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors -mb-px ${
            tab === "book" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <PackageOpen size={13} className="inline mr-1.5" /> Book Parcel
        </button>
        <button
          data-testid="tab-booking-history"
          onClick={() => setTab("history")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors -mb-px ${
            tab === "history" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <ClipboardList size={13} className="inline mr-1.5" /> Booking History
        </button>
      </div>

      {tab === "book" ? (
        <BookParcelTab />
      ) : (
        <>
      {/* KPI tiles for the pickup-booking workflow */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="booking-kpis">
          {[
            { label: "Pending pickups", value: stats.pending_pickups, tone: "border-blue-400 text-blue-700 bg-blue-50" },
            { label: "Delayed pickups", value: stats.delayed_pickups, tone: "border-amber-400 text-amber-700 bg-amber-50" },
            { label: "Picked · not booked", value: stats.picked_up_not_booked, tone: "border-indigo-400 text-indigo-700 bg-indigo-50" },
            { label: "SLA running", value: stats.sla_running, tone: "border-emerald-400 text-emerald-700 bg-emerald-50" },
            { label: "SLA breached", value: stats.sla_breached, tone: "border-red-500 text-red-700 bg-red-50" },
          ].map((k) => (
            <div key={k.label} className={`border rounded-md p-3 ${k.tone}`}
                 data-testid={`kpi-${k.label.toLowerCase().replaceAll(" ", "-")}`}>
              <div className="text-[10px] uppercase tracking-widest opacity-80">{k.label}</div>
              <div className="mono text-2xl font-bold mt-1">{k.value ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      <div className="border border-border rounded-md overflow-hidden bg-card" data-testid="bookings-table-wrap">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left overline bg-muted/30">
              <th className="p-3">Booking #</th>
              <th className="p-3">Courier / Service</th>
              <th className="p-3">Parcels</th>
              <th className="p-3">Pickup</th>
              <th className="p-3">Expected Delivery</th>
              <th className="p-3">Status</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="bookings-table">
            {items.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">
                No bookings yet. Select outward parcels on the Parcels page and click "Book Parcel".
              </td></tr>
            )}
            {items.map((b) => (
              <tr key={b.id} className="border-b border-border hover:bg-muted/40"
                  data-testid={`booking-row-${b.booking_number}`}>
                <td className="p-3 mono">
                  <div className="font-bold">{b.booking_number}</div>
                  <div className="text-[10px] text-muted-foreground">{b.booking_date}</div>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1 text-sm"><Truck size={12} /> {b.courier}</div>
                  <div className="text-[10px] text-muted-foreground">{b.service_type}</div>
                </td>
                <td className="p-3 mono">
                  {b.parcel_count} · {Number(b.total_weight_kg || 0).toFixed(2)} kg
                </td>
                <td className="p-3 text-xs">
                  <div>{b.pickup_date} {b.pickup_time}</div>
                  <div className="text-muted-foreground">{b.pickup_contact_name} · {b.pickup_contact_phone}</div>
                </td>
                <td className="p-3 mono text-xs">
                  {(b.expected_delivery_at || "").replace("T", " ").slice(0, 16) || "—"}
                </td>
                <td className="p-3">
                  <Select value={b.booking_status || "pickup_requested"}
                          onValueChange={(v) => setBookingStatus(b.id, v, b.booking_number)}>
                    <SelectTrigger data-testid={`booking-status-${b.booking_number}`}
                                   className={`h-7 rounded-md text-xs w-40 border ${STATUS_TONE[b.booking_status] || ""}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BOOKING_STATUS_CHOICES.map((s) => (
                        <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {b.email_sent && (
                    <div className="mt-1 flex items-center gap-1 text-[10px] text-emerald-700">
                      <Mail size={10} /> notified
                    </div>
                  )}
                </td>
                <td className="p-3 whitespace-nowrap">
                  <Button
                    size="sm" variant="outline"
                    data-testid={`booking-pdf-${b.booking_number}`}
                    onClick={() => downloadManifest(b)}
                    className="rounded-md"
                  >
                    <Download size={12} className="mr-1" /> Manifest
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ServerPagination
        page={page} pageSize={pageSize} total={total} pages={pages}
        onPageChange={(n) => setPage(n)}
        onPageSizeChange={(s) => { setPageSize(s); localStorage.setItem(LSKEY, String(s)); setPage(1); }}
        storageKey={LSKEY} testidPrefix="bookings-pg"
      />
        </>
      )}
    </PageShell>
  );
}

/* ------- Book Parcel tab (inline in Bookings module) ------------------ */
function BookParcelTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem("bookparcel.pageSize") || "50", 10));
  const [q, setQ] = useState("");
  const [selectedIds, setSelectedIds] = useState({});   // default empty — no auto-selection
  const [bookOpen, setBookOpen] = useState(false);

  const load = async (nextPage = page, nextSize = pageSize) => {
    const { data } = await api.get("/parcels-paginated", { params: {
      direction: "outward", q: q || undefined,
      page: nextPage, page_size: nextSize,
    }});
    setItems(data.items || []);
    setTotal(data.total || 0); setPages(data.pages || 1);
  };
  useEffect(() => { load(1, pageSize); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(page, pageSize); /* eslint-disable-next-line */ }, [page, pageSize]);

  const selectableIds = useMemo(
    () => items.filter(p => p.booking_status !== "delivered").map(p => p.id),
    [items]
  );
  const allOnPageSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds[id]);
  const selectedCount = Object.keys(selectedIds).filter(k => selectedIds[k]).length;

  const toggleSelectAll = (checked) => {
    // Only the current filtered/page records
    const next = { ...selectedIds };
    selectableIds.forEach(id => { next[id] = checked; });
    setSelectedIds(next);
  };

  const openBooking = () => {
    if (selectedCount === 0) {
      toast.error("Please select at least one parcel to continue.");
      return;
    }
    setBookOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="bp-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(1, pageSize); } }}
            className="pl-7 w-64 rounded-md"
            placeholder="Search tracking, sender, recipient…"
          />
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span data-testid="bp-selected-count" className="mono">
            <b className="text-foreground">{selectedCount}</b> selected · {total.toLocaleString()} outward parcels
          </span>
          <Button
            data-testid="bp-book-btn"
            onClick={openBooking}
            className="rounded-md bg-primary text-primary-foreground shadow-brut-orange"
          >
            <PackageOpen size={14} className="mr-1.5" /> Book Parcel{selectedCount > 0 ? ` · ${selectedCount}` : ""}
          </Button>
        </div>
      </div>

      <div className="border border-border bg-card overflow-x-auto rounded-md">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left overline bg-muted/30">
              <th className="p-3 w-8">
                <input type="checkbox" data-testid="bp-select-all" className="accent-[#BE1E1E]"
                  checked={allOnPageSelected}
                  onChange={(e) => toggleSelectAll(e.target.checked)} />
              </th>
              <th className="p-3">Tracking</th>
              <th className="p-3">AWB No.</th>
              <th className="p-3">Courier</th>
              <th className="p-3">Sender → Recipient</th>
              <th className="p-3">Type</th>
              <th className="p-3">Weight</th>
              <th className="p-3">Status</th>
              <th className="p-3">Booking</th>
            </tr>
          </thead>
          <tbody data-testid="bp-table">
            {items.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-sm text-muted-foreground">
                No outward parcels available for booking.
              </td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="border-b border-border hover:bg-muted/40" data-testid={`bp-row-${p.tracking_number}`}>
                <td className="p-3">
                  {p.booking_status !== "delivered" && (
                    <input type="checkbox"
                      data-testid={`bp-select-${p.tracking_number}`}
                      className="accent-[#BE1E1E]"
                      checked={!!selectedIds[p.id]}
                      onChange={(e) => setSelectedIds({ ...selectedIds, [p.id]: e.target.checked })} />
                  )}
                </td>
                <td className="p-3 mono text-xs font-semibold">
                  <div className="flex items-center gap-1.5">
                    {p.priority && <span className="inline-flex items-center gap-0.5 bg-primary text-primary-foreground text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"><Zap size={9} strokeWidth={3} /> P</span>}
                    <span>{p.tracking_number}</span>
                  </div>
                </td>
                <td className="p-3 mono text-xs" data-testid={`bp-awb-${p.tracking_number}`}>{p.awb_number || <span className="text-muted-foreground">—</span>}</td>
                <td className="p-3 mono text-xs">{p.courier || "—"}</td>
                <td className="p-3 text-xs">
                  <div>{p.sender_name}</div>
                  <div className="text-muted-foreground">→ {p.recipient_name}</div>
                </td>
                <td className="p-3 text-xs">{p.shipment_type || p.parcel_type || "—"}</td>
                <td className="p-3 mono text-xs">{p.weight_kg} kg</td>
                <td className="p-3">
                  <span className="text-[10px] uppercase tracking-widest border border-border px-1.5 py-0.5 rounded mono">{p.status}</span>
                </td>
                <td className="p-3 text-xs mono">{p.booking_number || <span className="text-muted-foreground">not booked</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ServerPagination
        page={page} pageSize={pageSize} total={total} pages={pages}
        onPageChange={(n) => setPage(n)}
        onPageSizeChange={(s) => { setPageSize(s); localStorage.setItem("bookparcel.pageSize", String(s)); setPage(1); }}
        storageKey="bookparcel.pageSize" testidPrefix="bp-pg"
      />

      <BookParcelDialog
        open={bookOpen}
        onClose={() => setBookOpen(false)}
        selectedParcels={items.filter(p => selectedIds[p.id])}
        onBooked={() => { setSelectedIds({}); setBookOpen(false); load(page, pageSize); }}
      />
    </div>
  );
}
